// 2026-06-08 (v50+) — per-channel default FAB-URL lookup.
//
// PRD has different upstream FAB hosts per main channel (ORF1=…34, ORF2=…35,
// ORF3=…33, KIDS=…36, ORFS=…37); the regional ORF2 splits (ORF-B/K/N/OOE/S/
// ST/T/V/W and ORF-SI which shares Tirol) all go through the shared host
// 10.128.16.38 with a `?language=ORF2<X>` query-string discriminator.
//
// DEV+STG keep all hosts at 10.128.16.38 (the dev FAB endpoint) but still
// emit the same `?language=…` query for regionals so live-test traffic
// hits the right Tirol/Vorarlberg/etc. virtual stream on the dev host.
//
// The lookup is wired from voxtral-schedule-dialog and the per-broadcast
// details panel: when the broadcast row has no explicit
// voxtralMini4bRealtimeFabUrl set, the operator-facing FAB-URL input
// is pre-filled with the channel's default. The operator's explicit edit
// always wins; this helper never auto-writes to the row.

import { environment } from '../../../../environments/environment';

const ORF2_DEFAULT_HOST = '10.128.16.38';

// PRD-specific main-channel hosts (the only entries that differ between
// PRD and DEV/STG — every regional uses the same shared host).
const PRD_MAIN_HOSTS: Readonly<Record<string, string>> = {
  ORF1: '10.128.16.34',
  ORF2: '10.128.16.35',
  ORF3: '10.128.16.33',
  KIDS: '10.128.16.36',
  ORFS: '10.128.16.37',
};

// Channel → ?language=… query-string discriminator. ORF-SI piggybacks on
// the Tirol stream per the production playbook.
const REGIONAL_LANG_PARAM: Readonly<Record<string, string>> = {
  'ORF-B': 'ORF2B',
  'ORF-K': 'ORF2K',
  'ORF-N': 'ORF2N',
  'ORF-NOE': 'ORF2N',  // codebase uses both spellings
  'ORF-OOE': 'ORF2O',
  'ORF-S': 'ORF2S',
  'ORF-ST': 'ORF2St',
  'ORF-T': 'ORF2T',
  'ORF-V': 'ORF2V',
  'ORF-W': 'ORF2W',
  'ORF-SI': 'ORF2T',
};

/**
 * Returns the channel-default FAB-URL for the current environment. When
 * the channel isn't in the table (or empty), returns the shared DEV/STG
 * fallback URL so the operator never sees a blank field.
 */
export function defaultFabUrlForChannel(channel: string | null | undefined): string {
  const ch = (channel || '').trim();
  const stage = (environment as { stage?: string }).stage || 'dev';
  const isPrd = stage === 'prd';

  // Regional ORF2 splits — always shared host, plus ?language=…
  const regional = REGIONAL_LANG_PARAM[ch];
  if (regional) {
    return `http://${ORF2_DEFAULT_HOST}/livetranscription/send?language=${regional}`;
  }

  // Main channels — PRD differs per channel; DEV/STG all share 10.128.16.38.
  if (isPrd && PRD_MAIN_HOSTS[ch]) {
    return `http://${PRD_MAIN_HOSTS[ch]}/livetranscription/send`;
  }

  // Fallback — shared dev FAB host, no query string.
  return `http://${ORF2_DEFAULT_HOST}/livetranscription/send`;
}
